import { useEffect, useState, useRef } from 'react';

export default function useOverlayPosition(contentRef, overlayRef) {
  const [isPositioned, setIsPositioned] = useState(false);
  const pollingRef = useRef(null);
  const rafRef = useRef(null);
  const isUpdating = useRef(false);

  useEffect(() => {
    const overlay = overlayRef?.current;
    const target = contentRef?.current;

    if (!overlay || !target) {
      return;
    }

    // Update overlay position based on target's bounding rect
    const update = () => {
      if (isUpdating.current) {
        return;
      }

      isUpdating.current = true;

      requestAnimationFrame(() => {
        const rect = target.getBoundingClientRect();
        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        overlay.style.top = `${rect.top + scrollTop}px`;
        overlay.style.left = `${rect.left + scrollLeft}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;

        setIsPositioned(true);
        isUpdating.current = false;
      });
    };

    // Polling for continuous updates during animation/transition
    const startPolling = () => {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(update, 10);
      }
    };

    const stopPolling = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        update(); // final update
      }
    };

    // Handlers for global animation/transition events
    const handleAnimStart = startPolling;
    const handleAnimEnd = stopPolling;

    // Smooth update on scroll using requestAnimationFrame
    const handleScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    // Observe target size changes with rAF wrapper to avoid ResizeObserver loop
    const resizeObserver = new ResizeObserver(() =>
      requestAnimationFrame(update),
    );
    resizeObserver.observe(target);

    // Observe DOM mutations with rAF wrapper
    const mutationObserver = new MutationObserver(() =>
      requestAnimationFrame(update),
    );
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    // Event listeners
    window.addEventListener('resize', update);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('animationstart', handleAnimStart, true);
    document.addEventListener('animationend', handleAnimEnd, true);
    document.addEventListener('transitionstart', handleAnimStart, true);
    document.addEventListener('transitionend', handleAnimEnd, true);

    update(); // initial positioning

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('animationstart', handleAnimStart, true);
      document.removeEventListener('animationend', handleAnimEnd, true);
      document.removeEventListener('transitionstart', handleAnimStart, true);
      document.removeEventListener('transitionend', handleAnimEnd, true);
      cancelAnimationFrame(rafRef.current);
      stopPolling();
    };
  }, [contentRef, overlayRef]);

  return isPositioned;
}
